import { SuggesterTokenSchema } from '@mixwire/contracts';
import { useEffect, useRef, useState, type ReactElement } from 'react';

import { LoadingStatus } from '../components/LoadingStatus.js';
import { useVerifyPasskey } from '../hooks/useVerifyPasskey.js';
import { PASSKEY_QUERY_PARAM, partyPath, suggestPath } from '../routing/paths.js';
import { suggesterTokenKey } from '../routing/suggesterToken.js';
import { useRoute } from '../routing/useRoute.js';

import { SuggesterPage } from './SuggesterPage.js';

import type { PartySlug, SuggesterToken } from '@mixwire/contracts';

const storedToken = (slug: PartySlug): SuggesterToken | null => {
  const parsed = SuggesterTokenSchema.safeParse(sessionStorage.getItem(suggesterTokenKey(slug)));
  return parsed.success ? parsed.data : null;
};

const invitePasskey = (): string | null =>
  new URLSearchParams(location.search).get(PASSKEY_QUERY_PARAM);

/**
 * Who is allowed to see the suggester view. A device that has already passed
 * the passcode check carries its token in sessionStorage; a device arriving
 * from a DJ's invite QR carries the passcode in the query instead, and trades
 * it for a token here. Anyone else is sent to the public view.
 */
export const SuggesterGate = ({ slug }: { readonly slug: PartySlug }): ReactElement | null => {
  const { navigate } = useRoute();
  const [grant, setGrant] = useState(() => storedToken(slug));
  const [passkey] = useState(invitePasskey);
  const verify = useVerifyPasskey();
  const draftHasRedeemed = useRef(false);

  const { mutate: redeem } = verify;

  useEffect(() => {
    if (grant !== null || passkey === null || draftHasRedeemed.current) {
      return;
    }

    draftHasRedeemed.current = true;
    // Before the request, not after it: the passcode must not sit on a
    // borrowed phone's screen or in its history for as long as a round trip.
    history.replaceState({}, '', suggestPath(slug));

    redeem(
      { slug, passkey },
      {
        onSuccess: (response) => {
          sessionStorage.setItem(suggesterTokenKey(slug), response.token);
          setGrant(response.token);
        },
      },
    );
  }, [slug, grant, passkey, redeem]);

  const isTurnedAway = grant === null && (passkey === null || verify.isError);

  useEffect(() => {
    if (isTurnedAway) {
      navigate(partyPath(slug));
    }
  }, [isTurnedAway, navigate, slug]);

  if (grant !== null) {
    return <SuggesterPage slug={slug} token={grant} />;
  }

  if (isTurnedAway) {
    return null;
  }

  return <LoadingStatus>Checking that passcode…</LoadingStatus>;
};
